import React, { useEffect, useState } from "react";
import { AssetType, useGetAssetsQuery } from "../store/slices/mapsSlice";
import { Button, Form, InputGroup } from "react-bootstrap";
import EditAssetModal from "./EditAssetModal";
import PageHeader from "../Common/PageHeader";
import Subheading from "../Common/Subheading";
import WhiteButton from "../Common/WhiteButton";
import WhiteButtonDropdown from "../Common/WhiteButtonDropdown";
import MyPagination from "../Common/MyPagination";
import AssetList from "./AssetList";
import AssetsIconSVG from "../../assets/icons/asset-01.svg2";
import AssetsLinks from "./AssetsLinks";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import { useNavigate, useParams } from "react-router-dom";
import {
  formatAssetKindOptionLabel,
  getAssetKindValueLabels,
  loadAssetKindOptions,
} from "./assetIconsList";
import HelpButton from "../HelpContent/HelpButton";
import AssetTypesModal from "./AssetTypesModal";
import ImportAssetsModal from "./ImportAssetsModal";
import ExportAssetsModal from "./ExportAssetsModal";
import DeleteAssetsModal from "./DeleteAssetsModal";
import {
  getAssetTypeOptionCacheVersion,
  subscribeAssetTypeOptionCache,
} from "./assetTypeOptions";

const newAsset: Partial<AssetType> = {
  Label: "",
  ClientId: 0,
  Kind: "asset",
  HasLocation: false,
  HasManyLocations: false,
  CanMove: false,
  HasLocationHistory: false,
  Locations: [],
  ParentAssets: [],
  ChildAssets: [],
  AssetContacts: [],
};

const assetFilterStorageKey = "workflows.assets.filters";
const assetFilterExpandedStorageKey = "workflows.assets.filters.expanded";
const assetShowTableStorageKey = "workflows.assets.showTable";

const defaultAssetFilters = {
  page: 0,
  perPage: 10,
  search: "new",
  kinds: [] as string[],
  hasLocation: false,
  hasNoLocation: false,
  hasContacts: false,
  hasNoContacts: false,
  hasDependants: false,
  hasNoDependants: false,
  hasConnected: false,
  hasNoConnected: false,
  hasActiveEvents: false,
  hasNoActiveEvents: false,
  orderBy: "created",
  order: "desc",
};

const loadAssetFilters = () => {
  try {
    const stored = window.localStorage.getItem(assetFilterStorageKey);
    if (!stored) {
      return defaultAssetFilters;
    }
    const parsed = JSON.parse(stored);
    return {
      ...defaultAssetFilters,
      ...parsed,
      page: 0,
      kinds: Array.isArray(parsed?.kinds) ? parsed.kinds : [],
    };
  } catch {
    return defaultAssetFilters;
  }
};

const loadAssetFiltersExpanded = () => {
  try {
    return window.localStorage.getItem(assetFilterExpandedStorageKey) === "true";
  } catch {
    return false;
  }
};

const loadAssetShowTable = () => {
  try {
    return window.localStorage.getItem(assetShowTableStorageKey) === "true";
  } catch {
    return false;
  }
};

export default function Assets() {
  const [pagination, setPagination] = useState(loadAssetFilters);
  const [search, setSearch] = useState(
    pagination.search === "new" ? "" : pagination.search
  );
  const [showTable, setShowTable] = useState(loadAssetShowTable);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(
    loadAssetFiltersExpanded
  );
  const [showAssetTypesModal, setShowAssetTypesModal] = useState(false);
  const [showImportAssetsModal, setShowImportAssetsModal] = useState(false);
  const [showExportAssetsModal, setShowExportAssetsModal] = useState(false);
  const [showDeleteAssetsModal, setShowDeleteAssetsModal] = useState(false);
  const [selectedAssetUuids, setSelectedAssetUuids] = useState<string[]>([]);
  const [assetTypeCacheVersion, setAssetTypeCacheVersion] = useState(
    getAssetTypeOptionCacheVersion()
  );
  useEffect(() => {
    const unsubscribe = subscribeAssetTypeOptionCache(setAssetTypeCacheVersion);
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setPagination((p) => ({ ...p, page: 0, search }));
    }, 500);
    return () => {
      clearTimeout(t);
    };
  }, [search]);

  useEffect(() => {
    window.localStorage.setItem(
      assetFilterStorageKey,
      JSON.stringify({ ...pagination, page: 0 })
    );
  }, [pagination]);

  useEffect(() => {
    window.localStorage.setItem(
      assetFilterExpandedStorageKey,
      String(showAdvancedFilters)
    );
  }, [showAdvancedFilters]);

  useEffect(() => {
    window.localStorage.setItem(assetShowTableStorageKey, String(showTable));
  }, [showTable]);

  const {
    data: { assets, total } = { assets: [], total: 0 },
    isLoading,
    error,
  } = useGetAssetsQuery(pagination);

  const assetRows = Array.isArray(assets) ? assets : [];

  const { assetUUID } = useParams();
  const navigate = useNavigate();
  const clearFilters = () => {
    setSearch("");
    setPagination({ ...defaultAssetFilters, search: "" });
  };
  const selectedAssets = assetRows.filter((asset) =>
    selectedAssetUuids.includes(asset.Uuid || "")
  );
  const paginationResetKey = JSON.stringify({
    search: pagination.search,
    kinds: pagination.kinds,
    hasLocation: pagination.hasLocation,
    hasNoLocation: pagination.hasNoLocation,
    hasContacts: pagination.hasContacts,
    hasNoContacts: pagination.hasNoContacts,
    hasDependants: pagination.hasDependants,
    hasNoDependants: pagination.hasNoDependants,
    hasConnected: pagination.hasConnected,
    hasNoConnected: pagination.hasNoConnected,
    hasActiveEvents: pagination.hasActiveEvents,
    hasNoActiveEvents: pagination.hasNoActiveEvents,
    orderBy: pagination.orderBy,
    order: pagination.order,
  });

  useEffect(() => {
    if (!showTable) {
      setSelectedAssetUuids([]);
    }
  }, [showTable]);

  const toggleAssetSelected = (uuid: string, selected: boolean) => {
    setSelectedAssetUuids((prev) => {
      if (selected) {
        return prev.includes(uuid) ? prev : [...prev, uuid];
      }
      return prev.filter((item) => item !== uuid);
    });
  };

  const togglePageSelected = (selected: boolean) => {
    const pageAssetUuids = assetRows
      .map((asset) => asset.Uuid)
      .filter(Boolean) as string[];

    setSelectedAssetUuids((prev) => {
      if (!selected) {
        return prev.filter((uuid) => !pageAssetUuids.includes(uuid));
      }
      return Array.from(new Set([...prev, ...pageAssetUuids]));
    });
  };

  return (
    <div className="mb-5">
      <div className="d-flex justify-content-between align-items-start">
        <div className="d-flex  align-items-center">
          <Subheading
            Icon={AssetsIconSVG}
            heading="Manage Assets"
            subheading="Add edit and control your companies Assets."
            helpKey="manage-assets"
          />
        </div>
        <WhiteButtonDropdown toggleHeading="Actions" toggleSubheading="Assets">
          <WhiteButton
            as="RRLink"
            to="/assets/assets/new"
            icon={AssetsIconSVG}
            heading="Add"
            subheading="Asset"
            target={undefined}
            disabled={false}
          />
          <WhiteButton
            as="button"
            icon={AssetsIconSVG}
            heading="Import"
            subheading="Assets"
            target={undefined}
            disabled={false}
            onClick={() => setShowImportAssetsModal(true)}
          />
          <WhiteButton
            as="button"
            icon={AssetsIconSVG}
            heading="Export"
            subheading="Assets"
            target={undefined}
            disabled={false}
            onClick={() => setShowExportAssetsModal(true)}
          />
          <WhiteButton
            as="button"
            icon={AssetsIconSVG}
            heading="Manage"
            subheading="Asset types"
            target={undefined}
            disabled={false}
            onClick={() => setShowAssetTypesModal(true)}
          />
        </WhiteButtonDropdown>
      </div>
      <style>{`
        .asset-filter-panel .asset-filter-checks .form-check {
          align-items: center;
          display: flex;
          gap: 0.4rem;
          margin: 0;
          padding-left: 0;
        }

        .asset-filter-panel .asset-filter-checks .form-check-input {
          float: none;
          margin-left: 0;
          margin-top: 0;
        }

        .asset-filter-panel .asset-type-filter-input,
        .asset-filter-panel .asset-type-filter-input .align-bottom,
        .asset-filter-panel .asset-type-filter-input .boom-input,
        .asset-filter-panel .asset-type-filter-input [data-select-input] {
          height: 100%;
        }

        .asset-filter-panel .asset-type-filter-input [data-select-input] > div {
          border-bottom-left-radius: 0;
          border-top-left-radius: 0;
          min-height: 38px;
        }

        .asset-filter-panel .asset-type-filter-input .react-select__control {
          border-bottom-left-radius: 0;
          border-top-left-radius: 0;
          min-height: 36px;
        }
      `}</style>
      <div className="asset-filter-panel mb-3 bg-colored-reverse p-3 m-0 rounded-3">
        <div className="d-flex flex-column gap-2">
          <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3">
            <div
              className="text-white fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center"
              style={{ width: 130 }}
            >
              Find
            </div>
            <div className="d-flex flex-row flex-wrap align-items-stretch gap-3 flex-grow-1">
              <div className="flex-grow-1" style={{ minWidth: 360, flex: "2 1 420px" }}>
                <InputGroup className="h-100">
                  <InputGroup.Text className="fw-bold bg-white text-dark border-light">Search</InputGroup.Text>
                  <Form.Control
                    aria-label="Search assets"
                    className="h-100"
                    style={{ minHeight: 38 }}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Reference, label or description"
                  />
                </InputGroup>
              </div>
              <div style={{ minWidth: 240, flex: "1 1 280px" }}>
                <InputGroup className="h-100">
                  <InputGroup.Text className="fw-bold bg-white text-dark border-light">
                    Asset type
                  </InputGroup.Text>
                  <div
                    className="flex-grow-1 d-flex"
                    style={{
                      minWidth: 0,
                      minHeight: 38,
                      overflow: "hidden",
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                  >
                    <Input
                      key={`asset-type-filter-${assetTypeCacheVersion}`}
                      className="asset-type-filter-input h-100"
                      value={pagination.kinds}
                      onChange={(kinds: string[]) => {
                        setPagination((p) => ({ ...p, page: 0, kinds }));
                      }}
                      inputType="select"
                      options={undefined}
                      loadOptions={loadAssetKindOptions as any}
                      formatOptionLabel={formatAssetKindOptionLabel}
                      getValueLabels={getAssetKindValueLabels as any}
                      isMulti
                      isClearable
                      placeholder="Select asset type"
                    />
                  </div>
                </InputGroup>
              </div>
            </div>
          </div>
          {showAdvancedFilters && (
            <>
              <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 text-white fw-bold d-flex flex-wrap align-items-center gap-3">
                <div className="small text-uppercase text-nowrap me-2" style={{ width: 130 }}>Location</div>
                <div className="asset-filter-checks d-flex flex-row flex-wrap align-items-center gap-3 flex-grow-1 ps-0">
                  <Form.Check
                    className="m-0"
                    type="checkbox"
                    label="Has location"
                    id="has-location"
                    checked={pagination.hasLocation}
                    onChange={(e) =>
                      setPagination((p) => ({
                        ...p,
                        page: 0,
                        hasLocation: e.target.checked,
                        hasNoLocation: e.target.checked ? false : p.hasNoLocation,
                      }))
                    }
                  />
                  <Form.Check
                    className="m-0"
                    type="checkbox"
                    label="Has no location"
                    id="has-no-location"
                    checked={pagination.hasNoLocation}
                    onChange={(e) =>
                      setPagination((p) => ({
                        ...p,
                        page: 0,
                        hasNoLocation: e.target.checked,
                        hasLocation: e.target.checked ? false : p.hasLocation,
                      }))
                    }
                  />
                </div>
              </div>
              <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 text-white fw-bold d-flex flex-wrap align-items-center gap-3">
                <div className="small text-uppercase text-nowrap me-2" style={{ width: 130 }}>Contacts</div>
                <div className="asset-filter-checks d-flex flex-row flex-wrap align-items-center gap-3 flex-grow-1 ps-0">
                  <Form.Check
                    className="m-0"
                    type="checkbox"
                    label="Has contacts"
                    id="has-contacts"
                    checked={pagination.hasContacts}
                    onChange={(e) =>
                      setPagination((p) => ({
                        ...p,
                        page: 0,
                        hasContacts: e.target.checked,
                        hasNoContacts: e.target.checked ? false : p.hasNoContacts,
                      }))
                    }
                  />
                  <Form.Check
                    className="m-0"
                    type="checkbox"
                    label="Has no contacts"
                    id="has-no-contacts"
                    checked={pagination.hasNoContacts}
                    onChange={(e) =>
                      setPagination((p) => ({
                        ...p,
                        page: 0,
                        hasNoContacts: e.target.checked,
                        hasContacts: e.target.checked ? false : p.hasContacts,
                      }))
                    }
                  />
                </div>
              </div>
              <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 text-white fw-bold d-flex flex-wrap align-items-center gap-3">
                <div className="small text-uppercase text-nowrap me-2" style={{ width: 130 }}>
                  Relationships
                </div>
                <div className="asset-filter-checks d-flex flex-row flex-wrap align-items-center gap-3 flex-grow-1 ps-0">
                  <Form.Check
                    className="m-0"
                    type="checkbox"
                    label="Has dependant assets"
                    id="has-dependants"
                    checked={pagination.hasDependants}
                    onChange={(e) =>
                      setPagination((p) => ({
                        ...p,
                        page: 0,
                        hasDependants: e.target.checked,
                        hasNoDependants: e.target.checked
                          ? false
                          : p.hasNoDependants,
                      }))
                    }
                  />
                  <Form.Check
                    className="m-0"
                    type="checkbox"
                    label="Has no dependant assets"
                    id="has-no-dependants"
                    checked={pagination.hasNoDependants}
                    onChange={(e) =>
                      setPagination((p) => ({
                        ...p,
                        page: 0,
                        hasNoDependants: e.target.checked,
                        hasDependants: e.target.checked ? false : p.hasDependants,
                      }))
                    }
                  />
                  <Form.Check
                    className="m-0"
                    type="checkbox"
                    label="Has connected assets"
                    id="has-connected"
                    checked={pagination.hasConnected}
                    onChange={(e) =>
                      setPagination((p) => ({
                        ...p,
                        page: 0,
                        hasConnected: e.target.checked,
                        hasNoConnected: e.target.checked ? false : p.hasNoConnected,
                      }))
                    }
                  />
                  <Form.Check
                    className="m-0"
                    type="checkbox"
                    label="Has no connected assets"
                    id="has-no-connected"
                    checked={pagination.hasNoConnected}
                    onChange={(e) =>
                      setPagination((p) => ({
                        ...p,
                        page: 0,
                        hasNoConnected: e.target.checked,
                        hasConnected: e.target.checked ? false : p.hasConnected,
                      }))
                    }
                  />
                </div>
              </div>
              <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 text-white fw-bold d-flex flex-wrap align-items-center gap-3">
                <div className="small text-uppercase text-nowrap me-2" style={{ width: 130 }}>Events</div>
                <div className="asset-filter-checks d-flex flex-row flex-wrap align-items-center gap-3 flex-grow-1 ps-0">
                  <Form.Check
                    className="m-0"
                    type="checkbox"
                    label="Has active events"
                    id="has-active-events"
                    checked={pagination.hasActiveEvents}
                    onChange={(e) =>
                      setPagination((p) => ({
                        ...p,
                        page: 0,
                        hasActiveEvents: e.target.checked,
                        hasNoActiveEvents: e.target.checked
                          ? false
                          : p.hasNoActiveEvents,
                      }))
                    }
                  />
                  <Form.Check
                    className="m-0"
                    type="checkbox"
                    label="Has no active events"
                    id="has-no-active-events"
                    checked={pagination.hasNoActiveEvents}
                    onChange={(e) =>
                      setPagination((p) => ({
                        ...p,
                        page: 0,
                        hasNoActiveEvents: e.target.checked,
                        hasActiveEvents: e.target.checked
                          ? false
                          : p.hasActiveEvents,
                      }))
                    }
                  />
                </div>
              </div>
              <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 text-white fw-bold d-flex flex-wrap align-items-end gap-3">
            <div className="small text-uppercase text-nowrap me-2 mb-1" style={{ width: 130 }}>
              Display
            </div>
            <div className="d-flex flex-row flex-wrap align-items-end gap-3">
              <div style={{ minWidth: 180 }}>
                <InputGroup>
                  <InputGroup.Text className="fw-bold bg-white text-dark border-light">Order by</InputGroup.Text>
                  <Form.Select
                    aria-label="Order assets by"
                    value={pagination.orderBy}
                    onChange={(e) =>
                      setPagination((p) => ({
                        ...p,
                        page: 0,
                        orderBy: e.target.value,
                      }))
                    }
                  >
                    <option value="created">Created</option>
                  <option value="name">Label</option>
                  </Form.Select>
                </InputGroup>
              </div>
              <div style={{ minWidth: 180 }}>
                <InputGroup>
                  <InputGroup.Text className="fw-bold bg-white text-dark border-light">Direction</InputGroup.Text>
                  <Form.Select
                    aria-label="Asset ordering direction"
                    value={pagination.order}
                    onChange={(e) =>
                      setPagination((p) => ({
                        ...p,
                        page: 0,
                        order: e.target.value,
                      }))
                    }
                  >
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </Form.Select>
                </InputGroup>
              </div>
              <div className="pb-2">
                <Form.Check
                  type="checkbox"
                  label="Show as table"
                  id="show-as-table"
                  checked={showTable}
                  onChange={(e) => setShowTable(e.target.checked)}
                />
              </div>
            </div>
          </div>
            </>
          )}
          <div className="d-flex justify-content-between align-items-center gap-3 pt-1">
            <div>
              {selectedAssetUuids.length > 0 && (
              <Button
                variant="danger"
                size="sm"
                className="text-nowrap px-3 py-1"
                onClick={() => setShowDeleteAssetsModal(true)}
              >
                Delete selected ({selectedAssetUuids.length})
              </Button>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-light"
                size="sm"
                className="text-nowrap px-3 py-1"
                onClick={clearFilters}
              >
                Clear filters
              </Button>
              <Button
                variant="outline-light"
                size="sm"
                className="text-nowrap px-3 py-1"
                onClick={() => setShowAdvancedFilters((expanded) => !expanded)}
              >
                {showAdvancedFilters ? "Less filters" : "More filters"}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="p-3 rounded-3 bg-white my-3">
        {total === 0 ? (
          <div className="alert alert-warning">No assets found.</div>
        ) : (
          <AssetList
            assets={assetRows}
            showTable={showTable}
            selectable={showTable}
            selectedAssetUuids={selectedAssetUuids}
            onToggleAssetSelected={toggleAssetSelected}
            onTogglePageSelected={togglePageSelected}
          />
        )}
        <MyPagination
          key={paginationResetKey}
          defaultPerPage={pagination.perPage}
          onPaginate={(page: number, perPage: number) =>
            setPagination((p) => ({ ...p, page, perPage }))
          }
          total={total}
        />
      </div>
      {assetUUID && (
        <EditAssetModal
          key={assetUUID}
          assetUuid={assetUUID}
          onClose={() => navigate("/assets/assets", { replace: true })}
          forceShow
        />
      )}
      <AssetTypesModal
        show={showAssetTypesModal}
        onHide={() => setShowAssetTypesModal(false)}
      />
      <ImportAssetsModal
        show={showImportAssetsModal}
        onHide={() => setShowImportAssetsModal(false)}
      />
      <ExportAssetsModal
        show={showExportAssetsModal}
        onHide={() => setShowExportAssetsModal(false)}
        filters={{
          search: pagination.search === "new" ? "" : pagination.search,
          kinds: pagination.kinds,
          hasLocation: pagination.hasLocation,
          hasNoLocation: pagination.hasNoLocation,
          hasContacts: pagination.hasContacts,
          hasNoContacts: pagination.hasNoContacts,
          hasDependants: pagination.hasDependants,
          hasNoDependants: pagination.hasNoDependants,
          hasConnected: pagination.hasConnected,
          hasNoConnected: pagination.hasNoConnected,
          orderBy: pagination.orderBy,
          order: pagination.order,
        }}
      />
      <DeleteAssetsModal
        show={showDeleteAssetsModal}
        assetUuids={selectedAssetUuids}
        assets={selectedAssets}
        onHide={() => setShowDeleteAssetsModal(false)}
        onDeleted={() => setSelectedAssetUuids([])}
      />
    </div>
  );
}
